import { useRouterContext } from "@rotorstudios/fe-modules";

import { useCmeContext } from "../fe-cme/CMEProvider";
import { useApiService } from "./useApiService";
import { useDataContext } from "./DataProvider";
import { GaEvent } from "../fe-modules/GA/static";
import { ACCESSORY_CATEGORY_EXTERIOR, CASH, MAIN_CATEGORY_VEHICLE, NONE } from "./static";
import { PAGE_HOME, PAGE_VEHICLE_SELECTION } from "../fe-modules/Router/static";
import useUpdateCookieData from "./useUpdateCookieData";
import useUrlParamLinks from "./useUrlParamLinks";
import useUserConfigPayloads from "./useUserConfigPayloads";
import useGA from "../fe-modules/GA/useGA";
import { EMAIL_VIEW, SIDEBAR_CONTENT } from "./enums";
import { useConfiguratorContext } from "../../contexts/ConfiguratorProvider";

const useUpdateUserData = () => {
  const { triggerGAEvent } = useGA();
  const { location, gotoPage } = useRouterContext();
  const { apiAddUserConfiguration, apiUpdateUserConfiguration, apiGetDealershipInfo } =
    useApiService();
  const {
    urlParams,
    pinData,
    vehicleData,
    dealershipData,
    dealerData,
    updateLoadingSequence,
    updateEmailState,
    updatePinData,
    updateVehicle,
    updateUserName,
    updateSidebarState,
    updateCurrentPricingType,
    updateConfiguratorState,
  } = useDataContext();
  const { updateSelectedUteAccessory } = useConfiguratorContext();
  const { getCurrentUserConfig, getMaterialCode } = useCmeContext();
  const {
    getBaseUrl,
    getDealerBaseUrl,
    getPinUrlParams,
    getConfigurationUrlParams,
    getDealerUrlParams,
    getVehicleUrlParams,
    createTFYLink,
  } = useUrlParamLinks();
  const { getAddConfigPayload, getUpdateConfigPayload } = useUserConfigPayloads();
  const { updateCookieVehicleConfig, updateCookieDealer, updateCookieUser } =
    useUpdateCookieData();

  // ********************
  // Url updates
  // ********************
  const handleUpdateUrl = (baseUrl: string, params: string, reload?: boolean) => {
    const newUrl = `${baseUrl}?${params}`;
    if (reload) {
      globalThis.window.location.href = newUrl;
    } else {
      globalThis.window.history.pushState(null, "", newUrl);
    }
  };

  const updatePinUrl = (pin: string) => {
    const baseUrl = getBaseUrl();
    const newParams = getPinUrlParams(pin);
    handleUpdateUrl(baseUrl, newParams);
  };

  const updateDealerPinUrl = (pin: string, url?: string) => {
    const baseUrl = getDealerBaseUrl(url);
    const newParams = getPinUrlParams(pin);
    handleUpdateUrl(baseUrl, newParams, true);
  };

  const updateDealerSapCodeUrl = (sapCode: string, url?: string) => {
    const baseUrl = getDealerBaseUrl(url);
    const newParams = getDealerUrlParams(sapCode);
    handleUpdateUrl(baseUrl, newParams, true);
  };

  // Update url params if mat code or accessories present already
  const updateVehicleConfigUrl = (matCode: string, accessories?: string[]) => {
    if (urlParams?.MC || urlParams?.accessories) {
      const baseUrl = getBaseUrl();
      const newParams = getConfigurationUrlParams(matCode, accessories);
      handleUpdateUrl(baseUrl, newParams);
    }
  };

  const updateVehicleUrl = (vehicle: string) => {
    const newParams = getVehicleUrlParams(vehicle);
    const shouldReplace = !location.pathname.includes(PAGE_VEHICLE_SELECTION);
    gotoPage(PAGE_HOME, { search: newParams }, shouldReplace);
  };

  // ********************
  // Add/Update pin data: url param
  // ********************
  const addOrUpdateUserConfigData = (userData: any) => {
    const handleResponse = (res: any, shouldUpdateUrl: boolean = false) => {
      const link = createTFYLink();
      navigator.clipboard
        .writeText(link)
        .then(() => console.log("Successfully copied to clipboard", link));
      triggerGAEvent(GaEvent.SUBMIT_SAVE_VEHICLE_FORM);
      updateEmailState(EMAIL_VIEW.SUCCESS);

      if (shouldUpdateUrl) {
        updatePinData({ pin: res.data?.pin, isValid: true });
        updatePinUrl(res.data?.pin);
      }
    };

    const handleError = (err: any, msg: string) => {
      updateEmailState(EMAIL_VIEW.ERROR);
      console.error(msg, err);
    };

    const payload = getAddConfigPayload(userData);

    if (pinData?.isValid) {
      apiUpdateUserConfiguration(pinData.pin, payload)
        .then((res: any) => handleResponse(res))
        .catch((err: any) =>
          handleError(err, "[useUpdateUserData]: Could not update user pin"),
        );
    } else {
      apiAddUserConfiguration(payload)
        .then((res: any) => handleResponse(res, true))
        .catch((err: any) =>
          handleError(err, "[useUpdateUserData]: Could not add user pin"),
        );
    }
  };

  // ********************
  // Update vehicle configuration: pin data or cookie/url params
  // ********************
  const updateVehicleConfigData = () => {
    const userConfig = getCurrentUserConfig();
    const materialCode = getMaterialCode();

    if (pinData?.isValid) {
      apiUpdateUserConfiguration(
        pinData.pin,
        getUpdateConfigPayload(userConfig, dealershipData.sapCode, dealerData),
      ).catch((err: any) =>
        console.error("[useUpdateUserData]: Could not update user pin", err),
      );
    } else {
      updateCookieVehicleConfig(
        vehicleData.vehicleId,
        materialCode.config,
        userConfig.accessories,
      );
      updateVehicleConfigUrl(materialCode.config, userConfig.accessories);
    }
  };

  // ********************
  // Update dealer: pin data or cookie/url params
  // ********************
  const updateDealershipData = async (sapCode: string) => {
    // Get updated dealership/dealer data using sap code
    const newDealershipData = await apiGetDealershipInfo(sapCode);

    if (pinData?.isValid) {
      const userConfig = getCurrentUserConfig();
      const newDealer = {
        firstName: newDealershipData?.conciergeFirstName,
        lastName: newDealershipData?.conciergeLastName,
        email: newDealershipData?.conciergeEmail,
      };
      const payload = getUpdateConfigPayload(userConfig, sapCode, newDealer);
      apiUpdateUserConfiguration(pinData.pin, payload)
        .then((res: any) =>
          updateDealerPinUrl(pinData.pin, newDealershipData?.nviWebsite),
        )
        .catch((err: any) =>
          console.error("[useUpdateUserData]: Could not update user pin", err),
        );
    } else {
      updateCookieDealer(sapCode);
      updateDealerSapCodeUrl(sapCode, newDealershipData?.nviWebsite);
    }
  };

  // ********************
  // Update vehicle: url params
  // ********************
  const updateVehicleData = (vehicleId: string) => {
    updateVehicleUrl(vehicleId);
    updateVehicle(vehicleId);
    updatePinData({ pin: "", isValid: false });
    updateCurrentPricingType(CASH);
    updateConfiguratorState({
      mainCategory: MAIN_CATEGORY_VEHICLE,
      accessoryCategoryId: ACCESSORY_CATEGORY_EXTERIOR,
      accessoryCategoryTitle: ACCESSORY_CATEGORY_EXTERIOR,
    });
    updateSelectedUteAccessory({
      id: NONE,
      type: NONE,
      configuration: NONE,
      colour: NONE,
    });
    updateLoadingSequence("");
  };

  // ********************
  // Update user name: cookie
  // ********************
  const updateUserNameData = (name?: string) => {
    updateUserName(name);
    updateCookieUser(name);
    updateSidebarState(SIDEBAR_CONTENT.DEFAULT);
  };

  return {
    addOrUpdateUserConfigData,
    updateVehicleConfigData,
    updateDealershipData,
    updateVehicleData,
    updateUserNameData,
    updateDealerSapCodeUrl,
  };
};

export default useUpdateUserData;
